import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Input, Row } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import { Link } from "react-router-dom";

const ItemWiseReport = ({ categoryData }) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Format helper
  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  // Fetch item-wise data
  const fetchData = async (
    from = fromDate,
    to = toDate,
    category = selectedCategory
  ) => {
    if (!from || !to) return;

    setLoading(true);
    try {
      const response = await SalesReportService.itemWise({
        categoryId: category?.value || null,
        fromDate: formatDate(from),
        toDate: formatDate(to),
      });
      setAllData(response || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize dates & categories on mount, auto-fetch last 1 month
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);

    // fetchCategoryData();
    fetchData(lastMonth, today, null); // initial fetch for last 1 month
  }, []);

  // Auto-fetch only when category changes
  useEffect(() => {
    if (fromDate && toDate) {
      fetchData(fromDate, toDate, selectedCategory);
    }
  }, [selectedCategory]); // only listen to category

  // totals
  const totalQuantity = allData.reduce(
    (sum, item) => sum + item.totalQuantity,
    0
  );
  const totalSales = allData.reduce((sum, item) => sum + item.totalSales, 0);

  // category dropdown options
  const categoryOption = categoryData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  return (
    <div>
      <Card className="shadow-sm mb-4">
        <CardBody>
          {/* Filters */}
          <Row className="mb-4 align-items-end">
            <Col md={4} className="mb-2">
              <Select
                isClearable
                options={categoryOption}
                value={selectedCategory}
                onChange={setSelectedCategory}
                placeholder="Filter by Category"
                className="fs-6"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
            <Col md={2} className="mb-2">
              <Flatpickr
                options={{ dateFormat: "Y-m-d" }}
                value={fromDate}
                onChange={(dates) => setFromDate(dates[0])}
                className="form-control"
                placeholder="From Date"
              />
            </Col>
            <Col md={2} className="mb-2">
              <Flatpickr
                options={{ dateFormat: "Y-m-d" }}
                value={toDate}
                onChange={(dates) => setToDate(dates[0])}
                className="form-control"
                placeholder="To Date"
              />
            </Col>
            <Col md={2} className="mb-2">
              <Button
                color="primary"
                className="w-100"
                onClick={() => fetchData()}
              >
                Generate
              </Button>
            </Col>
          </Row>

          {/* Loader */}
          {loading ? (
            <div className="my-5 text-center d-flex flex-column align-items-center">
              <Triangle
                visible={true}
                height="80"
                width="80"
                color="#5B71B9"
                ariaLabel="triangle-loading"
              />
              <h5 className="mt-3">Loading...</h5>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-primary sticky-top">
                  <tr>
                    <th style={{ width: "60px" }}>SN</th>
                    <th>Category Name</th>
                    <th>Item Name</th>
                    <th>Complimentry Qty</th>
                    <th style={{ width: "150px" }} className="text-end">
                      Total Quantity
                    </th>
                    <th style={{ width: "150px" }} className="text-end">
                      Total Sales
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allData.length > 0 ? (
                    allData.map((item, index) => (
                      <tr key={`${item.categoryId}-${item.itemName}`}>
                        <td>{index + 1}</td>
                        <td>{item.categoryName}</td>
                        <td><Link to={`/transaction/report/${item?.itemId}`}>{item.itemName}</Link></td>
                        <td className="text-end">
                          {item.complimentryQuantity}
                        </td>
                        <td className="text-end">{item.totalQuantity}</td>
                        <td className="text-end">
                          <NumberWithCommas number={item.totalSales} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
                {allData.length > 0 && (
                  <tfoot>
                    <tr className="table-secondary fw-bold text-end">
                      <td colSpan="4">Total</td>
                      <td>{totalQuantity}</td>
                      <td>
                        <NumberWithCommas number={totalSales} />
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ItemWiseReport;
